
import { computed, defineComponent, inject, onUnmounted, PropType, reactive, ref, toRefs, watch } from 'vue';
import { useVModel } from 'vue-composable';
import { v4 as uuid } from 'uuid';
import { DuplicateSurveyRequest, Survey } from '@/types';
import surveyService from '@/services/survey-service';
import RegionSelect from '@/components/inputs/RegionSelect.vue';
import { ShowToast } from '@/providers/Toast/toast-types';

/** Naive UI select values are strings, not booleans; translated on submit. */
type ContactVisibility = 'show' | 'hide';

type ModalState = {
  name: string;
  regionId: string | null;
  copyRecipients: boolean;
  shareable: boolean;
  contactVisibility: ContactVisibility;
  recipientCount: number;
  saving: boolean;
  submitError: string;
  regionsLoading: boolean;
  regionsFailed: boolean;
};

export default defineComponent({
  name: 'DuplicateSurveyModal',
  components: { RegionSelect },
  props: {
    survey: {
      type: Object as PropType<Survey | null>,
      default: null
    },
    showModal: {
      type: Boolean
    }
  },
  // update:showModal must be listed: undeclared events fall through and break v-model.
  emits: ['survey-duplicated', 'duplicate-started', 'duplicate-finished', 'update:showModal'],
  setup: (props, { emit }) => {
    const showModalRef = useVModel(props, 'showModal');
    const showToast = inject<ShowToast>('showToast')!;

    const shareOptions: { label: string; value: ContactVisibility }[] = [
      { label: 'Show Contacts', value: 'show' },
      { label: 'Hide Contacts', value: 'hide' }
    ];

    const state = reactive<ModalState>({
      name: '',
      regionId: null,
      copyRecipients: false,
      // A copy starts private with contacts hidden, whatever the source had.
      shareable: false,
      contactVisibility: 'hide',
      recipientCount: 0,
      saving: false,
      submitError: '',
      regionsLoading: false,
      regionsFailed: false
    });

    // Only a response whose generation is still current may touch the modal.
    let submitGeneration = 0;
    let unmounted = false;
    onUnmounted(() => {
      unmounted = true;
    });

    /** The idempotency key for the attempt in flight, so a retry resumes it rather than copying twice. */
    let requestId = '';

    const isValidName = computed(() => state.name.trim().length > 0);
    const canSubmit = computed(() => isValidName.value && !state.saving && !state.regionsLoading && !state.regionsFailed);

    const onRegionsLoading = (loading: boolean) => {
      state.regionsLoading = loading;
    };

    const onRegionsFailed = (failed: boolean) => {
      state.regionsFailed = failed;
    };

    // v-show, not v-if: the field must stay mounted or it could never load the regions that decide its own visibility.
    const noRegions = ref(false);
    const onRegionsEmpty = (empty: boolean) => {
      noRegions.value = empty;
    };

    // Sharing off always means contacts hidden.
    watch(
      () => state.shareable,
      (shareable) => {
        if (!shareable) state.contactVisibility = 'hide';
      }
    );

    // Reset on open so a previous edit cannot leak into the next survey.
    watch(
      () => props.showModal,
      async (isOpen) => {
        if (!isOpen || !props.survey) return;

        // Retire any in-flight request so its completion cannot take over this dialog.
        submitGeneration += 1;
        state.saving = false;

        state.name = `Copy of ${props.survey.name}`;
        state.regionId = props.survey.region?.id ?? null;
        state.copyRecipients = false;
        state.shareable = false;
        state.contactVisibility = 'hide';
        // Comes from the survey aggregates, so no extra request is needed here.
        state.recipientCount = props.survey.recipientCount ?? 0;
        state.submitError = '';
        requestId = '';
      }
    );

    const onCancel = () => {
      if (state.saving) return;

      showModalRef.value = false;
    };

    const onDuplicate = async () => {
      if (!props.survey || !canSubmit.value) return;

      const { survey } = props;
      submitGeneration += 1;
      const attempt = submitGeneration;

      // Reused across retries so the server resumes one copy instead of creating another.
      if (!requestId) requestId = uuid();

      state.saving = true;
      state.submitError = '';

      const request: DuplicateSurveyRequest = {
        requestId,
        name: state.name.trim(),
        regionId: state.regionId ?? undefined,
        copyRecipients: state.copyRecipients,
        shareable: state.shareable,
        // Sharing off always means contacts hidden.
        hideContactsOnShare: !state.shareable || state.contactVisibility === 'hide'
      };

      emit('duplicate-started', survey.id);

      try {
        const result = await surveyService.duplicate(survey.id, request);

        if (unmounted || attempt !== submitGeneration) return;

        showModalRef.value = false;
        requestId = '';
        emit('survey-duplicated', result.survey);
        showToast({
          headline: 'Success',
          message: result.recipientsComplete
            ? 'Survey Duplicated'
            : `Survey Duplicated, but only ${result.recipientsCopied} of ${result.recipientsRequested} recipients were copied. Duplicating again will finish the list.`,
          color: result.recipientsComplete ? 'success' : 'warning'
        });
      } catch {
        if (unmounted || attempt !== submitGeneration) return;

        // Stays open so a retry reuses the same requestId and cannot make a second copy.
        state.submitError = 'Failed to duplicate survey. Please try again.';
        showToast({
          headline: 'Error',
          message: 'Failed to duplicate survey',
          color: 'danger'
        });
      } finally {
        if (!unmounted && attempt === submitGeneration) state.saving = false;
        emit('duplicate-finished', survey.id);
      }
    };

    return {
      ...toRefs(state),
      showModalRef,
      shareOptions,
      isValidName,
      canSubmit,
      onRegionsLoading,
      onRegionsFailed,
      onRegionsEmpty,
      noRegions,
      onCancel,
      onDuplicate
    };
  }
});
